import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LockKeyhole } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BrandMark } from "@/components/BrandLogo";
import { Layout } from "@/components/Layout";
import { Seo } from "@/components/Seo";
import { useAuth } from "@/context/AuthContext";

export function AdminLoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    const result = await login(email, password);
    setLoading(false);
    if (result.ok) navigate("/admin"); else setError(result.error);
  };

  return (
    <Layout>
      <Seo title="Owner login" description="Private login for the Operandi Studio store owner." />
      <section className="flex min-h-screen items-center justify-center bg-[#111315] px-6 py-32 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(217,119,6,0.18),transparent_30%),radial-gradient(circle_at_80%_70%,rgba(63,94,77,0.22),transparent_32%)]" />
        <form onSubmit={submit} data-testid="admin-login-form" className="relative w-full max-w-md rounded-[2.5rem] border border-white/10 bg-white/[0.06] p-8 shadow-2xl backdrop-blur-xl md:p-10">
          <BrandMark className="h-14 w-14" />
          <p className="mt-8 font-mono text-xs uppercase tracking-[0.24em] text-amber-500">Private owner area</p>
          <h1 className="mt-3 font-heading text-3xl font-extrabold tracking-tight">Manage the storefront.</h1>
          <p className="mt-3 text-sm leading-7 text-white/62">Sign in to add products, attach delivery files, review messages, and inspect orders.</p>
          {error && <div data-testid="admin-login-error" className="mt-6 rounded-2xl border border-red-400/30 bg-red-500/10 p-4 text-sm text-red-100">{error}</div>}
          <label className="mt-7 grid gap-2 text-sm font-semibold">Email
            <Input type="email" required value={email} onChange={(event) => setEmail(event.target.value)} data-testid="admin-email-input" className="h-12 rounded-2xl border-white/10 bg-white/10 text-white placeholder:text-white/35" />
          </label>
          <label className="mt-5 grid gap-2 text-sm font-semibold">Password
            <Input type="password" required value={password} onChange={(event) => setPassword(event.target.value)} data-testid="admin-password-input" className="h-12 rounded-2xl border-white/10 bg-white/10 text-white placeholder:text-white/35" />
          </label>
          <Button type="submit" disabled={loading || user === undefined} className="mt-7 h-12 w-full rounded-full bg-[#D97706] text-white hover:bg-[#B45309]" data-testid="admin-login-submit-button">
            <LockKeyhole className="h-4 w-4" /> {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </section>
    </Layout>
  );
}
